<template>
  <div class="view" v-if="canEdit" v-cloak>
    <EditCreateTitleBox :label="title" />

    <div class="storage-losses-add-edit-container">
      <StepsPanelWrapper
        ref="stepsWrapper"
        :steps="steps"
        :current-step.sync="currentStep"
        :step-props-fn="getStepProps"
        @step-input="onStepInput"
        @validation-error="handleValidationError"
      />

      <StorageLossRightPanel :value="formData" :saving="saving" @save="save" @back="gotoList" />
    </div>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { canEdit, permissions, permissionTypes } from "~/helpers/Permissions";
import Forbidden from "@/pages/errors/Forbidden";
import EditCreateTitleBox from "@/components/Common/EditCreateTitleBox.vue";
import StepsPanelWrapper from "@/components/Common/StepsPanelWrapper.vue";
import settings from "./partials/settings.vue";
import lossRows from "./partials/lossRows.vue";
import documentOptions from "./partials/documentOptions.vue";
import StorageLossRightPanel from "./partials/StorageLossRightPanel.vue";
import { successMessage, errorMessage, handlePromiseException } from "@/helpers/common";
import { format } from "date-fns";

export default {
  name: "StorageLossEdit",
  components: {
    Forbidden,
    EditCreateTitleBox,
    StepsPanelWrapper,
    settings,
    lossRows,
    documentOptions,
    StorageLossRightPanel,
  },
  data() {
    return {
      title: this.$t("stock_management.storage_losses.create_new"),
      saving: false,
      currentStep: 1,
      formData: {
        uuid: null,
        storage_loss_date: null,
        legal_entity_uuid: null,
        legal_entity_name: null,
        warehouse_uuid: null,
        warehouse_name: null,
        product_uuid: null,
        product_name: null,
        product_code: null,
        product_lot_management: null,
        lots: [],
        label_uuids: [],
        engineer_uuid: null,
        engineer_name: null,
        warehouse_manager_uuid: null,
        warehouse_manager_name: null,
        note: null,
        rows: [],
        generate_document: false,
      },
      steps: [
        { number: 1, component: settings, label: this.$t("common.settings"), ref: "StorageLossSettings" },
        { number: 2, component: lossRows, label: this.$t("stock_management.storage_losses.rows"), ref: "StorageLossRows" },
        { number: 3, component: documentOptions, label: this.$t("stock_management.storage_losses.document"), ref: "StorageLossDocument" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      quantityDecimalPlaces: "settings/getQuantityDecimalPlaces",
    }),
    config() {
      return {
        permission: permissions.SM_STORAGE_LOSSES,
        permission_type: permissionTypes.access,
      };
    },
    canEdit() {
      return canEdit(this);
    },
  },
  mounted() {
    if (this.$route.params.uuid && this.$route.params.uuid !== "add") {
      this.title = this.$t("stock_management.storage_losses.edit");
      this.fetchData(this.$route.params.uuid);
    }
  },
  methods: {
    ...mapActions({
      getSmStorageLossByUuid: "smStorageLosses/getSmStorageLossByUuid",
      createSmStorageLoss: "smStorageLosses/createSmStorageLoss",
      updateSmStorageLoss: "smStorageLosses/updateSmStorageLoss",
    }),
    getStepProps() {
      return { formData: this.formData, quantityDecimalPlaces: this.quantityDecimalPlaces };
    },
    onStepInput(step, payload) {
      this.formData = { ...this.formData, ...(payload || {}) };
    },
    handleValidationError(error) {
      errorMessage(this, error?.message || this.$t("fill_out_error"));
    },
    async fetchData(uuid) {
      try {
        const response = await this.getSmStorageLossByUuid({ uuid });
        this.formData = { ...this.formData, ...(response.data || {}) };
      } catch (e) {
        handlePromiseException(this, e);
      }
    },
    async save() {
      const wrapper = this.$refs?.stepsWrapper;
      if (wrapper && typeof wrapper.validateSteps === "function") {
        const errors = await Promise.resolve(wrapper.validateSteps());
        if (errors && errors.length) {
          errors.forEach((msg) => errorMessage(this, msg));
          return;
        }
      }

      const positiveRows = (Array.isArray(this.formData?.rows) ? this.formData.rows : []).filter(
        (row) => Number(row?.loss_quantity || 0) > 0,
      );
      if (!positiveRows.length) {
        errorMessage(this, this.$t("validation.products_and_quantity_required"));
        return;
      }

      this.saving = true;
      const payload = {
        ...this.formData,
        rows: positiveRows,
        storage_loss_date: this.formatDateOnly(this.formData.storage_loss_date),
      };
      try {
        if (payload.uuid) {
          const response = await this.updateSmStorageLoss(payload);
          successMessage(this, response.message);
        } else {
          const response = await this.createSmStorageLoss(payload);
          successMessage(this, response.message);
        }
        this.gotoList();
      } catch (e) {
        handlePromiseException(this, e);
      } finally {
        this.saving = false;
      }
    },
    gotoList() {
      this.$router.push({ name: "storage-losses" });
    },
    formatDateOnly(value) {
      if (!value) return value;
      if (typeof value === "string") return value;
      return format(value, "yyyy-MM-dd");
    },
  },
};
</script>

<style scoped>
.storage-losses-add-edit-container {
  display: flex;
  gap: 16px;
  height: 100%;
  width: 100%;
  overflow-x: hidden;
  overflow-y: hidden;
}

.storage-losses-add-edit-container > :first-child {
  flex: 1 1 auto;
  min-width: 0;
}

.view {
  height: calc(100% - 70px);
}
</style>
